/**
 * Feature catalog — source of truth for all app features.
 *
 * Keys are stable identifiers (never rename a key once shipped — it orphans grants).
 * UUIDs are generated by the DB on first insert. No hardcoded IDs here.
 *
 * FeatureSyncService (backend) keeps the DB in sync with this list on startup:
 *   - INSERT new keys
 *   - UPDATE changed displayName/description
 *   - DELETE removed keys (cascades ResourcePermission grants)
 *
 * Guards look up feature IDs at runtime via FeatureSyncService in-memory cache.
 *
 * Also the vocabulary for `RoleGroupGrant.surface` (NEX-2514 member-view slice):
 * a UserGroup's narrower-than-full-Role grant names one of these keys — "a CS
 * staff group gets inbox access only" is `AppFeature.INBOX.key`.
 */
import { z } from "zod";

export const AppFeature = {
  INBOX: {
    key: "inbox",
    displayName: "Inbox",
    description: "Conversation inbox"
  },
  PLAYGROUND: {
    key: "playground",
    displayName: "Playground",
    description: "Agent testing"
  },
  AGENTS: {
    key: "agents",
    displayName: "Agents",
    description: "Agent management"
  },
  WORKFLOWS: {
    key: "workflows",
    displayName: "Workflows",
    description: "Workflow builder"
  },
  CONTACTS: {
    key: "contacts",
    displayName: "Contacts",
    description: "Customer contacts directory"
  },
  DEPLOYMENTS: {
    key: "deployments",
    displayName: "Deployments",
    description: "Channel deployments"
  },
  DOCUMENTS: {
    key: "documents",
    displayName: "Documents",
    description: "Document library"
  },
  TOOLS: {
    key: "tools",
    displayName: "Tools",
    description: "Tool integrations"
  },
  KNOWLEDGE: {
    key: "knowledge",
    displayName: "Knowledge",
    description: "Knowledge bases"
  },
  CUE: {
    key: "cue",
    displayName: "Cue",
    description: "AI assistant side panel"
  },
  CUSTOMER_VIEWS: {
    key: "customer_views",
    displayName: "Customer Views",
    description: "Saved views with filters, sorts, groups, and column config for contacts"
  },
  WORKSPACES: {
    key: "workspaces",
    displayName: "Workspaces",
    description: "Org-shared file workspaces — agents mount, admins manage in-app"
  },
  APPS: {
    key: "apps",
    displayName: "Apps",
    description: "Internal apps — build, deploy and monitor them"
  },
  OVERVIEW: {
    key: "overview",
    displayName: "Overview",
    description: "Organisation system map — every resource the org's workflows reach"
  }
} as const;

export type AppFeatureEntry = (typeof AppFeature)[keyof typeof AppFeature];
export type AppFeatureKey = AppFeatureEntry["key"];

/** All feature entries as an array — used by FeatureSyncService. */
export const APP_FEATURE_VALUES: readonly AppFeatureEntry[] = Object.values(AppFeature);

/** Tuple for Zod: z.enum(APP_FEATURE_KEYS) */
export const APP_FEATURE_KEYS = APP_FEATURE_VALUES.map((feature) => feature.key) as [
  AppFeatureKey,
  ...AppFeatureKey[]
];

export const AppFeatureKeySchema = z.enum(APP_FEATURE_KEYS);

/**
 * The wire code for "this feature is not granted to you", as sent by
 * `FeatureAccessGuard` and branched on by the frontend error handler.
 *
 * It lives HERE, rather than beside the backend error class, because both sides
 * of the HTTP boundary have to agree on it and the frontend cannot import from
 * `apps/backend`. The alternative — the string typed out again on the frontend —
 * is the drift this package already exists to prevent, and the sibling
 * `FEATURE_NOT_ENABLED` code is currently spelled that way in
 * `roleReadState.ts`, with no compile error available if either copy changes.
 *
 * Renaming this is a breaking wire change: a client still branching on the old
 * spelling silently falls back to a generic "you don't have permission", which
 * is precisely the dead end it was introduced to remove.
 */
export const FEATURE_ACCESS_RESTRICTED_CODE = "FEATURE_ACCESS_RESTRICTED";

/**
 * Where one catalog feature stands for one user group.
 *
 * A group with no feature grants is UNRESTRICTED — every member reaches every
 * feature — so for it nothing is granted and nothing is new. A group with one
 * or more grants is limited to exactly those, and a feature added to the
 * catalog AFTER the group became restricted is withheld from its members. That
 * withholding is the policy, not a defect; what an admin must be able to see is
 * that nobody ever DECIDED it:
 *
 *   - `granted`          — the group holds a grant on the feature
 *   - `not_granted`      — no grant, and an admin decided that (or the group is
 *                          unrestricted, where "not granted" withholds nothing)
 *   - `new_not_granted`  — no grant, the group is restricted, and no admin has
 *                          decided about this feature since it joined the
 *                          catalog. Members are refused it.
 *
 * "Decided" is a stored acknowledgement per (group, feature), written when a
 * group first becomes restricted (for the whole catalog it was shown), on every
 * grant or revoke of that feature for that group, and by the explicit
 * acknowledge action. A revoke deletes the grant row, so without that record a
 * deliberately removed feature and one that never existed when the grants were
 * written would read the same.
 */
export const GroupFeatureStatus = {
  GRANTED: "granted",
  NOT_GRANTED: "not_granted",
  NEW_NOT_GRANTED: "new_not_granted"
} as const;

export type GroupFeatureStatusValue = (typeof GroupFeatureStatus)[keyof typeof GroupFeatureStatus];

export const GroupFeatureStatusSchema = z.enum([
  GroupFeatureStatus.GRANTED,
  GroupFeatureStatus.NOT_GRANTED,
  GroupFeatureStatus.NEW_NOT_GRANTED
]);

/**
 * An admin's answer to a feature on one group: grant it, or decline it and
 * keep it withheld. Either answer clears `new_not_granted`.
 */
export const GroupFeatureDecision = {
  GRANT: "grant",
  DECLINE: "decline"
} as const;

export type GroupFeatureDecisionValue =
  (typeof GroupFeatureDecision)[keyof typeof GroupFeatureDecision];

export const GroupFeatureDecisionSchema = z.enum([
  GroupFeatureDecision.GRANT,
  GroupFeatureDecision.DECLINE
]);

/**
 * The `details` of a `FEATURE_ACCESS_RESTRICTED` error envelope — what the
 * refused caller needs in order to ask an admin for the right thing.
 *
 * `restrictedBy` names WHY the caller's access is limited: their own direct
 * feature grants (`userGrant`), and every group they belong to that holds
 * feature grants (`groups`). A group listed here does not grant the refused
 * feature — otherwise the caller would not have been refused — and
 * `featureStatus` says whether an admin decided that (`not_granted`) or the
 * feature arrived after the group's access was set and nobody has looked at it
 * yet (`new_not_granted`).
 *
 * Group names are the caller's own memberships; nothing here names another user.
 */
export const FeatureAccessRestrictedDetailsSchema = z.object({
  featureKey: AppFeatureKeySchema,
  featureName: z.string(),
  restrictedBy: z.object({
    userGrant: z.boolean(),
    groups: z.array(
      z.object({
        id: z.string(),
        name: z.string(),
        featureStatus: z.enum([GroupFeatureStatus.NOT_GRANTED, GroupFeatureStatus.NEW_NOT_GRANTED])
      })
    )
  })
});

export type FeatureAccessRestrictedDetails = z.infer<typeof FeatureAccessRestrictedDetailsSchema>;
